import { JOINT_ACTIONS, type JointActionInfo, type Zone } from "./coverageVocabulary";
import {
  BIARTICULAR_AMBIGUITY,
  GOAL_REQUIREMENTS,
  JOINT_HEALTH_FLOOR,
  RETIRED_GOALS,
  type GoalRequirement,
  type RequiredCell,
} from "./goalRequirements";
import {
  cellKey,
  describedExercises,
  primeVerdict,
  reading,
  servable,
  undescribed,
  type CellReading,
  type CoverageRow,
  type MatrixInput,
  type PrimeVerdict,
} from "./coverage";

/* His §6.2, on a screen — *"the feature nothing else has"*.
 *
 * `docs/JOINT-ACTION-ENGINE.md` §6.2, in the owner's own words: *"When an athlete adopts a goal,
 * the engine diffs required cells against actual coverage and reports the gap before a single
 * session is generated. That is the difference between 'train hard and hope' and 'here is the
 * specific range you are not training, and here is what fills it.'"*
 *
 * ── WHAT IS GENERATED AND WHAT IS WRITTEN TWICE ───────────────────────────────────────────
 *
 * `goalRequirements.ts` is GENERATED from `JointActionGoalRequirements.swift`: the 107 required
 * cells, the five-cell joint-health floor, the eight ambiguous cells and the retired-goal
 * translation have exactly one authorship, and a harness ruler regenerates the file every run.
 * A hand-typed second copy of a hundred biomechanical judgements is this repo's defining bug
 * class pre-installed — and it would fail in the FLATTERING direction, because a cell dropped in
 * transcription reads as a goal with one fewer requirement, i.e. as a cleaner report.
 *
 * The LADDER below is written in TypeScript as well as in Swift, which is `coverage.ts`'s own
 * honest cost restated: the Swift harness cannot execute TypeScript, and a rule with no runner
 * is a comment with a type annotation. Both sides are executed — `goalGaps.test.ts` here,
 * `joint-action-4-goals-declare-their-cells` there — and `joint-action-5`'s source rulers assert
 * this file names every state Swift has and applies the shared threshold rather than a dose.
 *
 * ⚠️ NOTHING HERE IS READ BY GENERATION, on either side. His §8 puts that at step 5 and warns
 * against attempting steps 1–5 in one pass; `joint-action-1`'s ruler 8 asserts it as an absence
 * over every file the phone's harness compiles.
 */

/** §10.2, and it is his decision rather than a setting. See `standardFor`. */
export type CompletenessStandard = "athleteChosenGoals" | "fullOptimization";

/**
 * §12.4's six states, because each names a DIFFERENT thing to do.
 *
 * A single score would blend *"nothing trains this"* with *"you told us to stop training this"*
 * and a coach would act on neither correctly — the ranking rule `beta-watch`'s digest and the web
 * roster both landed on independently.
 */
export type GapState =
  /** At or above `MINIMUM_WEIGHTED_SETS`. Not a gap; omitted from `gaps`. */
  | "met"
  /** Met, and the reading cannot say WHICH TISSUE met it — §12.5's biarticular ambiguity. */
  | "metAmbiguous"
  /** Trained, under the minimum. */
  | "thin"
  /** Nothing trains it. The finding. */
  | "zero"
  /** The week holds undescribed exercises, so anything short of met is unproven. */
  | "unknown"
  /** 🔴 The athlete deferred this cell and a goal they hold requires it. A conflict for a person. */
  | "blockedByDeferral";

/** What a stored `profiles.goals` string turned out to be. */
export type GoalAnswerKind =
  /** A live `TrainingGoal`. */
  | "goal"
  /** A retired value that means an EMPHASIS today, so it declares no cells. */
  | "emphasis"
  /** Nothing in the phone's vocabulary. Reported, never dropped. */
  | "unrecognised";

export type GoalAnswer = {
  /** The exact string stored on the profile. */
  readonly stored: string;
  /** The live goal it resolves to, or null. */
  readonly goal: string | null;
  readonly requirement: GoalRequirement | null;
  readonly kind: GoalAnswerKind;
  /** Set only when the stored string was retired, so the screen can say what happened to it. */
  readonly translatedFrom: string | null;
};

export type RequirementReading = {
  readonly required: RequiredCell;
  readonly action: JointActionInfo;
  readonly reading: CellReading;
  readonly state: GapState;
  /** 🔴 Can anything in the athlete's own LIBRARY fill it? §12.6's audit set, given the right
   *  argument: a cell nothing described can serve is a library finding with a different owner. */
  readonly servableByLibrary: boolean;
  /** 🔴 AND BY WHAT — §14.13's verdict, asked at LIBRARY scope. `servableByLibrary` says a cell is
   *  fillable; this says whether anything in the library actually DRIVES it, or whether the only
   *  thing that would fill it is stabiliser weight at a quarter of a set. See `primeableByLibrary`
   *  for why the verdict is carried rather than a flag. */
  readonly libraryPrimeVerdict: PrimeVerdict;
  /** §12.5's sentence when the cell is ambiguous, so the screen can say why `met` is qualified. */
  readonly ambiguity: string | null;
  /** Which goals asked for it. A cell wanted by three goals is not the same finding as one
   *  wanted by the joint-health floor alone, and the screen must be able to say which. */
  readonly askedBy: readonly string[];
};

export type GapSummary = {
  readonly required: number;
  readonly met: number;
  readonly metAmbiguous: number;
  readonly thin: number;
  readonly zero: number;
  readonly unknown: number;
  readonly blockedByDeferral: number;
  /** Gaps a described exercise in the library could fill. The actionable half. */
  readonly fillable: number;
  /** 🔴 …AND HOW MANY OF THOSE A PRIME MOVER WOULD FILL. The rest are fillable only by
   *  synergist and stabiliser weight — 0.25 a set for a brace, so eight sets where a driving row
   *  needs two — which is `fillable` overstating rather than lying, and the two numbers side by
   *  side are what stop it. Never larger than `fillable`. */
  readonly fillableByPrimeMover: number;
};

/**
 * §10.2, read rather than offered.
 *
 * ⚠️ THE STANDARD FOLLOWS WHO IS HOLDING THE WHEEL, AND THE DATABASE ALREADY RECORDS WHO.
 * `profiles.uses_full_optimization` is the athlete's own answer to "coach chooses for me", so
 * this is a stored preference ADDING to what is known rather than contradicting a measurement —
 * the distinction `TrainingAuthority` exists to enforce, where a stored `coaching_source` was
 * allowed to overrule a coach who was demonstrably writing the athlete's training.
 *
 * A toggle was considered and refused: which standard applies is a fact about the athlete, not a
 * view the coach picks, and offering both on one screen would make it answer two questions.
 */
export function standardFor(usesFullOptimization: boolean): CompletenessStandard {
  return usesFullOptimization ? "fullOptimization" : "athleteChosenGoals";
}

/**
 * What each stored goal string means today.
 *
 * ⚠️ THREE ANSWERS, NOT TWO, AND THE THIRD IS LIVE. `profiles.goals` holds raw strings written by
 * whatever version of the app the athlete last used. Measured 2026-08-30, one real profile still
 * stores `"Core Strength"` — retired in favour of an EMPHASIS — so a resolver with only
 * goal/not-a-goal would report an unrecognised goal for an athlete the phone reads perfectly.
 * `RETIRED_GOALS` is emitted from `StoredGoal` for exactly that reason.
 *
 * An unrecognised string is REPORTED and never silently dropped: it is the one case where the
 * screen genuinely does not know what the athlete wants, and a gap report that quietly ignores
 * part of the athlete's stated intent is the flattering failure this whole layer refuses.
 */
export function resolveStoredGoals(stored: readonly string[]): GoalAnswer[] {
  return stored.map((raw): GoalAnswer => {
    const retired = Object.prototype.hasOwnProperty.call(RETIRED_GOALS, raw)
      ? RETIRED_GOALS[raw]
      : undefined;
    if (retired !== undefined) {
      if (retired.kind === "emphasis") {
        return {
          stored: raw, goal: null, requirement: null,
          kind: "emphasis", translatedFrom: retired.emphasis,
        };
      }
      if (retired.kind === "mains") {
        // "General Fitness" (retired 2026-09-10) is the five MAINS: on the phone the row is
        // rewritten by migration into each main's base goal, every one of which declares no cell
        // of its own — so for the gap report it is translated and SAID, never counted, exactly
        // like an emphasis. The base goals are named so the screen can say what it became.
        return {
          stored: raw, goal: null, requirement: null,
          kind: "emphasis", translatedFrom: retired.goals.join(" · "),
        };
      }
      const req = GOAL_REQUIREMENTS[retired.goal];
      return {
        stored: raw, goal: retired.goal, requirement: req ?? null,
        kind: req === undefined ? "unrecognised" : "goal", translatedFrom: retired.goal,
      };
    }
    const direct = Object.prototype.hasOwnProperty.call(GOAL_REQUIREMENTS, raw)
      ? GOAL_REQUIREMENTS[raw]
      : undefined;
    if (direct === undefined) {
      return { stored: raw, goal: null, requirement: null, kind: "unrecognised", translatedFrom: null };
    }
    return { stored: raw, goal: raw, requirement: direct, kind: "goal", translatedFrom: null };
  });
}

/** Goals that share one answer, and the sentence that answer carries. */
export type GoalGroup = {
  readonly kind: GoalAnswerKind | GoalRequirement["kind"];
  readonly headline: string;
  /** The claim, once, however many goals share it. Empty when the kind carries none. */
  readonly why: string;
  readonly goals: readonly string[];
};

/**
 * The goals, grouped by the answer they got.
 *
 * ⚠️ GROUPED RATHER THAN LISTED, BECAUSE THE SENTENCES REPEAT VERBATIM. `Strength` and
 * `Muscle Growth` carry the SAME paragraph — *"there is no exercise that IS muscle growth, all
 * work is"* — and printing it twice under two headings reads as a rendering fault rather than as
 * one reason two goals share. Grouping on (kind, why) says it once and names both.
 *
 * ⚠️ AND THE ORDER IS THE ATHLETE'S OWN. Groups appear in the order their first goal was stored,
 * so a screen never reorders somebody's stated priorities to suit its own layout.
 */
export function groupGoalAnswers(answers: readonly GoalAnswer[]): GoalGroup[] {
  const out: GoalGroup[] = [];
  const index = new Map<string, number>();
  for (const a of answers) {
    const kind: GoalGroup["kind"] =
      a.kind === "goal" && a.requirement !== null ? a.requirement.kind : a.kind;
    const why =
      a.requirement !== null
        && (a.requirement.kind === "noZoneRequirement" || a.requirement.kind === "needsMobilityAxis")
        ? a.requirement.why
        : "";
    const key = kind + " " + why;
    const at = index.get(key);
    if (at === undefined) {
      index.set(key, out.length);
      out.push({ kind, headline: goalGroupHeadline(kind), why, goals: [a.stored] });
    } else {
      const g = out[at];
      if (g !== undefined) out[at] = { ...g, goals: [...g.goals, a.stored] };
    }
  }
  return out;
}

/** What each answer means, in a phrase a coach reads rather than a case name. */
export function goalGroupHeadline(kind: GoalGroup["kind"]): string {
  switch (kind) {
    case "declared": return "Declares the ranges it is made of";
    case "noZoneRequirement": return "Names no movement, so it requires no range of its own";
    case "needsMobilityAxis": return "Measured in degrees rather than sets — not yet expressible";
    case "notYetDescribed": return "Nobody has described this goal yet";
    case "emphasis": return "An emphasis rather than a goal now, so it declares no ranges";
    case "unrecognised": return "This app does not recognise this goal";
    case "goal": return "Declares the ranges it is made of";
  }
}

/** The live goals among a resolution, in the order the athlete stored them. */
export function recognisedGoals(answers: readonly GoalAnswer[]): string[] {
  const out: string[] = [];
  for (const a of answers) {
    if (a.kind === "goal" && a.goal !== null && !out.includes(a.goal)) out.push(a.goal);
  }
  return out;
}

/**
 * The cells a standard requires of an athlete, deduplicated, goal order preserved.
 *
 * ⚠️ A CELL REQUIRED TWICE KEEPS ITS STRONGEST EMPHASIS — `defining` beats `supporting`. Taking
 * the first would make the report depend on the order the athlete happened to pick their goals
 * in, which is not a fact about their training.
 *
 * ⚠️ AND THE FULL-OPTIMIZATION ARM IS OVER THE **SERVABLE** CELLS, NEVER ALL 118. His §6.3 read
 * literally — warn on every zero in the taxonomy — is a wall of warnings nobody reads, which is
 * this repo's own recorded failure mode; a cell no described exercise can fill is a LIBRARY
 * finding with a different owner, exactly `WeeklyVolumeLedger.poolServes`' `Calves` verdict.
 */
export function requiredCells(
  goals: readonly string[],
  standard: CompletenessStandard,
  library: MatrixInput["rows"],
): { cells: RequiredCell[]; askedBy: Map<string, string[]> } {
  const cells: RequiredCell[] = [];
  const askedBy = new Map<string, string[]>();
  const index = new Map<string, number>();

  const add = (c: RequiredCell, by: string) => {
    const key = cellKey(c.action, c.zone);
    const at = index.get(key);
    const askers = askedBy.get(key) ?? [];
    if (!askers.includes(by)) askers.push(by);
    askedBy.set(key, askers);
    if (at === undefined) {
      index.set(key, cells.length);
      cells.push(c);
      return;
    }
    const existing = cells[at];
    if (existing !== undefined && c.emphasis === "defining" && existing.emphasis === "supporting") {
      cells[at] = c;
    }
  };

  for (const goal of goals) {
    const req = GOAL_REQUIREMENTS[goal];
    if (req !== undefined && req.kind === "declared") {
      for (const c of req.cells) add(c, goal);
    }
  }
  for (const c of JOINT_HEALTH_FLOOR) add(c, JOINT_HEALTH_FLOOR_LABEL);

  if (standard === "fullOptimization") {
    const canFill = servable(library);
    for (const a of JOINT_ACTIONS) {
      for (const z of a.zones) {
        if (!canFill.has(cellKey(a.id, z))) continue;
        add(
          {
            action: a.id, zone: z, emphasis: "supporting",
            why: "the complete-athlete standard: the athlete asked the coach to choose "
               + "everything, so a cell their own library could fill and nothing does is an "
               + "accidental zero the engine owes them",
          },
          COMPLETE_ATHLETE_LABEL,
        );
      }
    }
  }
  return { cells, askedBy };
}

/** How the floor and the complete-athlete sweep name themselves in `askedBy`. Not goals. */
export const JOINT_HEALTH_FLOOR_LABEL = "Joint-health floor";
export const COMPLETE_ATHLETE_LABEL = "Complete-athlete standard";

/**
 * Every required cell WITH its state — the diff his §6.2 asks for.
 *
 * ⚠️ IT RETURNS THE MET ONES TOO, WHICH SWIFT'S `gaps` DOES NOT, AND THE REASON IS THE READER.
 * A screen that shows only holes cannot say *"seven of your eleven required ranges are covered"*,
 * and a bare list of four problems reads as a verdict on the whole programme. `gaps` below is the
 * filter, so the two ends still agree about what a gap IS.
 *
 * ⚠️ AN ACTION THE VOCABULARY DOES NOT KNOW IS REPORTED, NOT DROPPED. It is impossible while both
 * generated files come from one Swift table — but a silently skipped requirement is a gap report
 * that got quieter for a reason nobody can see, which is the exact shape this layer refuses.
 */
export function requirementReadings(
  goals: readonly string[],
  standard: CompletenessStandard,
  input: MatrixInput,
): { readings: RequirementReading[]; unresolved: string[] } {
  const { cells, askedBy } = requiredCells(goals, standard, input.rows);
  const complete = undescribed(input).length === 0;
  const canFill = servable(input.rows);
  // 🔴 LIBRARY SCOPE, AND THE FIELD'S NAME SAYS SO. `reachable` is every exercise the library
  // describes, so the question is *"does the catalogue prime this"* and not *"can this athlete
  // reach something that does"*. That makes `primedOnlyOutOfReach` unreachable by construction
  // here — honest, because nothing is out of reach when nothing is filtered, and pinned by
  // `joint-action-4`'s ruler 12 rather than left to be noticed.
  //
  // ⚠️ THE ATHLETE HALF IS NAMED, NOT FAKED. Measured, `spine.anti_flexion · mid` is primed by
  // four rows that every one name a barbell, a dumbbell or a kettlebell, so 4 of 9 goldens cannot
  // reach it while the catalogue is fine — a different owner, and answering it needs the
  // athlete's filtered pool. This screen reads the coverage table and the week and never runs the
  // exercise filter, so that answer waits for a caller that can ask it.
  const described = describedExercises(input.rows);
  const byId = new Map<string, JointActionInfo>(JOINT_ACTIONS.map((a) => [a.id, a]));

  const readings: RequirementReading[] = [];
  const unresolved: string[] = [];
  for (const required of cells) {
    const action = byId.get(required.action);
    const key = cellKey(required.action, required.zone);
    if (action === undefined || !action.zones.includes(required.zone)) {
      unresolved.push(key);
      continue;
    }
    const r = reading(action, required.zone, input, complete);
    const ambiguity = Object.prototype.hasOwnProperty.call(BIARTICULAR_AMBIGUITY, key)
      ? BIARTICULAR_AMBIGUITY[key] ?? null
      : null;
    readings.push({
      required, action, reading: r,
      state: gapStateOf(r.state, ambiguity !== null),
      servableByLibrary: canFill.has(key),
      libraryPrimeVerdict: primeVerdict(required.action, required.zone, input.rows, described),
      ambiguity,
      askedBy: askedBy.get(key) ?? [],
    });
  }
  return { readings, unresolved };
}

/**
 * The ladder, and it is `JointActionGoalRequirements.gaps`' arms in the same order.
 *
 * ⚠️ THE DEFERRAL ARM COMES FIRST AND THAT IS LOAD-BEARING. A deferred cell a goal requires is
 * never `met` — which would hide a conflict behind a measurement, and his own false grip really
 * does put weighted sets into the forearm-supination cell he suspended — and never `zero`, which
 * would delete the athlete's own statement. Both wrong answers are available and both look fine.
 */
export function gapStateOf(state: CellReading["state"], ambiguous: boolean): GapState {
  switch (state) {
    case "zeroDeclared":
    case "declaredButTrained":
      return "blockedByDeferral";
    case "covered":
      return ambiguous ? "metAmbiguous" : "met";
    case "thin":
      return "thin";
    case "zeroAccidental":
      return "zero";
    case "provisional":
      return "unknown";
  }
}

/**
 * 🔴 THE SIBLING OF `servableByLibrary`, AND IT IS DERIVED RATHER THAN STORED.
 *
 * `Gap.primeableByLibrary` on the Swift side is a computed property over the same verdict, for the
 * same reason: a flag stored beside the verdict is a counter kept alongside its own record, and
 * this repo has paid for that shape (`SessionProgress`' cache with two writers, where which call
 * ran last decided whether the athlete could finish their workout).
 *
 * ⚠️ AND THE VERDICT IS WHAT IS CARRIED, BECAUSE THE FLAG ALONE WOULD BLEND TWO OWNERS. `false`
 * means either *the catalogue has no movement whose defining job is this* — a content decision,
 * and the owner's — or *nobody has described one yet*, which §14.13 measures as the weakest of
 * the four answers. A screen that cannot tell them apart states a confidence the model does not
 * have.
 */
export function primeableByLibrary(r: Pick<RequirementReading, "libraryPrimeVerdict">): boolean {
  return r.libraryPrimeVerdict === "primed";
}

/**
 * What the verdict means for THIS gap, in one sentence — `null` when there is nothing to say.
 *
 * ⚠️ IT LIVES HERE AND NOT IN THE COMPONENT, AND THAT IS A RULE THIS PAGE ALREADY PAID FOR: a
 * paragraph assembled from constants inside a component rendered starting with a LOWERCASE word,
 * invisible in review and obvious in a screenshot. A string the tests can drive is a string
 * something checks.
 *
 * `primed` returns `null` rather than a reassurance. The screen answers ONE question — what is
 * missing — and a line under every fillable row saying it is fine is the *"extra bs"* §2b rejects.
 */
export function primeVerdictNote(verdict: PrimeVerdict): string | null {
  switch (verdict) {
    case "primed":
      return null;
    case "primedOnlyOutOfReach":
      return "Something in the library drives this range, but nothing this athlete can reach — "
           + "an equipment question rather than a gap in their programme.";
    case "describedNeverPrimed":
      return "Nothing in the library DRIVES this range. Everything described here works it as a "
           + "synergist or a stabiliser, which earns a quarter of a set — so filling it from what "
           + "exists means eight sets where a driving movement would need two. That is a gap in "
           + "the library rather than in this athlete's week.";
    case "undescribed":
      return "No exercise has been described against this range at all, so the library cannot be "
           + "said to lack a movement for it — only that nobody has written one down yet.";
  }
}

/** The non-met readings — Swift's `gaps` exactly, expressed as a filter over the whole set. */
export function gaps(readings: readonly RequirementReading[]): RequirementReading[] {
  return readings.filter((r) => r.state !== "met");
}

/**
 * Rank, and it is a CATEGORY ordering rather than a score — `beta-watch`'s digest and the roster
 * both refused a weighting for the same reason. Each state names a different thing to DO, and a
 * number would blend them into one dial nobody could act on:
 *
 *   blockedByDeferral → talk to the athlete; nothing the engine does can settle it
 *   zero              → put the movement in the week
 *   thin              → add volume to what is already there
 *   metAmbiguous      → check WHICH exercise is covering it
 *   unknown           → describe the exercises nobody has described
 *
 * Within a state, `defining` before `supporting`: a cell the skill IS outranks one it needs.
 * Everything else keeps `requiredCells`' order, which is the athlete's own goal order.
 */
export const GAP_STATE_RANK: Readonly<Record<GapState, number>> = {
  blockedByDeferral: 0,
  zero: 1,
  thin: 2,
  metAmbiguous: 3,
  unknown: 4,
  met: 5,
};

export function sortGaps(readings: readonly RequirementReading[]): RequirementReading[] {
  return readings
    .map((r, i) => ({ r, i }))
    .sort((a, b) => {
      const s = GAP_STATE_RANK[a.r.state] - GAP_STATE_RANK[b.r.state];
      if (s !== 0) return s;
      const ea = a.r.required.emphasis === "defining" ? 0 : 1;
      const eb = b.r.required.emphasis === "defining" ? 0 : 1;
      if (ea !== eb) return ea - eb;
      return a.i - b.i;
    })
    .map((x) => x.r);
}

/** What a state says, in one phrase. The screen never spells a state's name at a reader. */
export function gapStateLabel(state: GapState): string {
  switch (state) {
    case "met": return "Covered";
    case "metAmbiguous": return "Covered, but not sure by what";
    case "thin": return "Thin";
    case "zero": return "Nothing trains this";
    case "unknown": return "Not yet known";
    case "blockedByDeferral": return "Deferred, and a goal needs it";
  }
}

/** What to DO about it — the sentence that justifies the ranking above being a ranking. */
export function gapAction(state: GapState): string {
  switch (state) {
    case "met": return "Nothing.";
    case "metAmbiguous":
      return "Check which exercise is covering it — the second joint decides which muscle is "
           + "the prime mover, and this reading cannot tell you which one you trained.";
    case "thin": return "Add volume to what is already there.";
    case "zero": return "Put the movement in the week.";
    case "unknown":
      return "Describe the exercises nobody has described yet — until then this cannot be "
           + "called a hole.";
    case "blockedByDeferral":
      return "Talk to the athlete. They suspended this range and a goal they hold needs it, "
           + "which is not something the engine may settle on their behalf.";
  }
}

export function summariseGaps(readings: readonly RequirementReading[]): GapSummary {
  const count = (s: GapState) => readings.filter((r) => r.state === s).length;
  return {
    required: readings.length,
    met: count("met"),
    metAmbiguous: count("metAmbiguous"),
    thin: count("thin"),
    zero: count("zero"),
    unknown: count("unknown"),
    blockedByDeferral: count("blockedByDeferral"),
    fillable: readings.filter((r) => r.state !== "met" && r.servableByLibrary).length,
    fillableByPrimeMover:
      readings.filter((r) => r.state !== "met" && primeableByLibrary(r)).length,
  };
}

/**
 * ⚠️ AN EMPTY WEEK AND A WEEK THAT TRAINS NOTHING RELEVANT ARE DIFFERENT, AND THE STATES CANNOT
 * TELL THEM APART. Nothing assigned means every required cell reads `zero` — correctly, since
 * nothing trains it — but presented without this the report reads as an indictment of a
 * programme rather than as *"there is no programme in this window"*. Measured on the coach's own
 * roster: one of his two clients has ZERO assignments in the last seven days, so this is the
 * first thing his second client's screen has to say rather than a defensive edge case.
 */
export function weekIsEmpty(input: MatrixInput): boolean {
  return input.week.length === 0;
}

/** A cell's athlete-facing name. Built from the vocabulary, never from a second label table. */
export function cellLabel(action: JointActionInfo, zone: Zone): string {
  return `${action.label} · ${zone}`;
}

/** One exercise that could fill a cell, and the role it would play in it. */
export type Filler = {
  readonly exerciseId: string;
  readonly name: string;
  readonly role: CoverageRow["role"];
};

/**
 * 🔴 THE SECOND HALF OF HIS §6.2's SENTENCE — *"and here is what fills it"*.
 *
 * `servableByLibrary` says a cell is fillable; this says BY WHAT. §12.6 names four such cells on
 * the owner's own week (`DB Row` for anti-rotation, `Back Extension` for loaded spine extension,
 * `Farmer's Carry` for scapular elevation, `Jump Rope` for dorsiflexion at length), and a report
 * that knows the cell is fillable and will not name the exercise makes the coach go looking for
 * something the model has already found.
 *
 * ⚠️ RANKED BY ROLE, AND THE ROLE IS SHOWN RATHER THAN HIDDEN BEHIND A YES. A prime mover earns a
 * whole weighted set and a stabiliser a quarter, so "this would fill it" means eight sets for a
 * bracing row and two for a driving one. §11's `hasNoPrimeMover` exists because a cell can clear
 * the minimum on bracing alone, and offering a stabiliser as though it were an answer would put
 * that same misreading into the fix rather than into the diagnosis.
 *
 * ⚠️ AND IT NAMES ONLY EXERCISES THE MODEL HAS DESCRIBED. An undescribed exercise might fill the
 * cell perfectly and cannot be offered, which is the monotonicity rule pointing the other way:
 * the list is a lower bound on what the library can do, never a claim about what it cannot.
 */
/**
 * ⚠️ THE LIST IS CAPPED FOR DISPLAY, AND ONLY RENDERING SHOWED WHY.
 *
 * `fillers` is complete on purpose — it is the rule, and a rule that silently drops candidates is
 * a worse thing than a long list. But rendered against the coach's real library, one gap
 * (`spine.anti_extension, MID`) named **more than a dozen exercises on one row**, and a screen
 * with five gaps became a wall of names: the *"extra bs"* §2b rejects, and the wall-of-warnings
 * failure mode this whole model keeps refusing, arriving in the FIX rather than the diagnosis.
 *
 * The remainder is COUNTED rather than dropped, because "here are three of eleven" and "here are
 * three" are different claims — and the sort is prime-first, so the three shown are the ones that
 * would fill the cell in two sets rather than eight.
 */
export function topFillers(all: readonly Filler[], limit = 3): { shown: Filler[]; more: number } {
  return { shown: all.slice(0, limit), more: Math.max(0, all.length - limit) };
}

export function fillers(
  action: string,
  zone: Zone,
  library: MatrixInput["rows"],
  names: ReadonlyMap<string, string>,
): Filler[] {
  const rank: Record<CoverageRow["role"], number> = { prime: 0, synergist: 1, stabiliser: 2 };
  const seen = new Set<string>();
  const out: Filler[] = [];
  for (const r of library) {
    if (r.action !== action || r.zone !== zone) continue;
    if (seen.has(r.exerciseId)) continue;
    seen.add(r.exerciseId);
    out.push({ exerciseId: r.exerciseId, name: names.get(r.exerciseId) ?? "Unnamed", role: r.role });
  }
  return out.sort((a, b) => {
    const d = rank[a.role] - rank[b.role];
    return d !== 0 ? d : a.name.localeCompare(b.name);
  });
}
